<template>
  <c-box position="relative">
    <c-flex
      v-if="selectedCity"
      border-radius="input"
      border="1px solid"
      border-color="inherit"
      px="4"
      height="12"
      font-size="1rem !important"
      width="100%"
      justify-content="space-between"
      align-items="center"
      bg="brand.blueLightest"
    >
      <c-text as="span" class="name">{{ selectedCity }}</c-text>
      <button class="delete" @click.prevent="clearSearch()">
        <c-icon name="trashcan" size="1.5rem" />
      </button>
    </c-flex>
    <c-input
      v-if="!selectedCity"
      id="searchTerm"
      size="lg"
      v-model.trim="searchTerm"
      :placeholder="placeholder || $t('citySearch.placeholder')"
      @keydown.up.prevent.stop="selectPrevByKeyboard()"
      @keydown.down.prevent.stop="selectNextByKeyboard()"
      @keydown.enter="enterPressed()"
      @keydown.esc="clearSearch()"
      @focus="
        showResults();
        $event.target.select();
        $emit('focus', $event);
      "
      v-chakra="
        (theme) => ({
          '&::placeholder': {
            color: 'brand.purple',
          },
        })
      "
    />
    <slot name="after-input"></slot>
    <c-box v-if="loading" class="results-wrapper" position="absolute" width="100%" z-index="10">
      <c-flex
        justify-content="center"
        border-radius="input"
        background="white"
        box-shadow="base"
        py="md"
        border-color="brand.gray"
        v-chakra="
          (theme) => ({
            borderWidth: theme.border.base,
          })
        "
      >
        <Spinner />
      </c-flex>
    </c-box>
    <c-box
      v-else-if="resultsVisible && searchResults"
      class="results-wrapper"
      position="absolute"
      width="100%"
    >
      <c-box
        data-cy="quick-city-search-results"
        border-radius="input"
        background="white"
        box-shadow="base"
        position="relative"
        z-index="10"
        border-color="brand.gray"
        v-chakra="
          (theme) => ({
            borderWidth: theme.border.base,
          })
        "
      >
        <!-- Tried to add border-bottom-width with v-chakra callback from theme,
        but it didn't work when selectedIndex was active and the whole border
        went missing -> had to hardcode the value to make it work... -->
        <c-box
          v-for="(city, index) in searchResults"
          :key="city"
          @click.prevent.stop="pickCity(city)"
          :cursor="'pointer'"
          font-weight="300"
          py="md"
          :px="index === selectedIndex && 'xl'"
          :mx="index === selectedIndex ? '0' : 'xl'"
          :background="index === selectedIndex && 'brand.gray'"
          :color="index === selectedIndex && 'brand.purpleDark'"
          overflow="auto"
          line-height="1.1"
          :_hover="{
            background: 'brand.gray',
            color: 'brand.purpleDark',
            mx: '0',
            px: 'xl',
          }"
          border-bottom-color="gray.200"
          border-bottom-width="1px"
        >
          <c-text as="span" float="left" width="100%" pr="sm">{{ `${city}` }}</c-text>
        </c-box>
        <c-box
          v-if="searchResults.length > 0"
          color="brand.grayDark"
          font-weight="300"
          py="sm"
          px="xl"
          overflow="auto"
        >
          {{ $t("quickSearch.displayingFirstOnes") }}
        </c-box>
        <c-box
          v-if="searchResults.length === 0"
          color="brand.grayDark"
          font-weight="300"
          py="sm"
          px="xl"
          overflow="auto"
        >
          {{ $t("citySearch.noResults") }}
        </c-box>
      </c-box>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import AddressValidationApi from "@/api/AddressValidationApi";
import Spinner from "@/components/Spinner.vue";
import { CBox, CFlex, CIcon, CInput, CText } from "@chakra-ui/vue";
import _ from "lodash";
import { defineComponent } from "vue";

export default defineComponent({
  name: "QuickCitySearch",
  components: {
    Spinner,
    CBox,
    CIcon,
    CInput,
    CFlex,
    CText,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    setPickedCityToField: {
      type: Boolean,
      default: true,
    },
    inputClass: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    includeArchived: {
      type: Boolean,
      default: undefined,
    },
    city: {
      type: String,
      default: "",
    },
    zipCode: {
      type: String,
      default: "00000",
    },
    countryCode: {
      type: String,
      default: "",
    },
    isPickup: {
      type: Boolean,
      default: false,
    },
    isDHL: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      searchTerm: "",
      lastSearchTerm: "",
      typing: false,
      loading: false,
      selectedCity: this.city || "",
      searchResults: [],
      selectedIndex: -1,
      resultsVisible: true,
      externalChangeOngoing: false,
      addressValidationApi: new AddressValidationApi(),
    };
  },
  watch: {
    value(newValue) {
      if (this.searchTerm !== newValue) {
        this.externalChangeOngoing = true;
        this.searchTerm = newValue;
      }
    },
    searchTerm(newTerm) {
      if (this.externalChangeOngoing) {
        this.externalChangeOngoing = false;
        return;
      }

      this.$emit("update:value", newTerm);
      this.$emit("field-change");
      if (newTerm.trim() !== (this.lastSearchTerm || "").trim()) {
        this.typing = true;
        this.resultsVisible = true;
        this.searchResults = [];
        this.search(newTerm);
      }
    },
  },
  mounted() {
    this.pickCity(this.city);
    this.resultsVisible = false;
  },
  methods: {
    search: _.debounce(function (this: any, term: string) {
      if (term !== this.lastSearchTerm) {
        this.lastSearchTerm = term;

        if (term.trim().length > 0) {
          this.loading = true;
          this.addressValidationApi
            .validateAddressAndGetCities(
              this.zipCode,
              this.countryCode,
              this.isDHL,
              this.isPickup,
              term
            )
            .then((results: string[]) => {
              if (term === this.lastSearchTerm) {
                this.searchResults = results;
                this.selectedIndex = -1;
              }
            })
            .finally(() => {
              this.loading = false;
            });
        } else {
          this.searchResults = [];
        }

        this.typing = false;
      }
    }, 400),
    pickCity(city) {
      this.resultsVisible = false;
      if (this.setPickedCityToField) {
        this.searchTerm = `${city}`;
        this.lastSearchTerm = this.searchTerm;
        this.searchResults = [];

        this.$emit("update:searchTerm", this.searchTerm);
        this.$emit("field-change");
      }
      this.selectedCity = city;
      this.$emit("set-delivery-city", this.selectedCity);
    },
    selectPrevByKeyboard() {
      if (this.searchResults.length) {
        this.selectedIndex -= 1;
        if (this.selectedIndex < 0) {
          this.selectedIndex = this.searchResults.length - 1;
        }
      }
    },
    selectNextByKeyboard() {
      if (this.searchResults.length) {
        this.selectedIndex += 1;
        if (this.selectedIndex >= this.searchResults.length) {
          this.selectedIndex = 0;
        }
      }
    },
    enterPressed() {
      if (this.resultsVisible) {
        this.pickCityByKeyboard();
      } else {
        this.resultsVisible = true;
      }
    },
    pickCityByKeyboard() {
      if (this.selectedIndex >= 0 && this.searchResults.length) {
        this.pickCity(this.searchResults[this.selectedIndex]);
      }
    },
    hideResults() {
      setTimeout(() => {
        this.resultsVisible = false;
        this.$emit("field-blur");
      }, 300);
    },
    showResults() {
      this.resultsVisible = true;
    },
    clearSearch() {
      this.searchTerm = "";
      this.$emit("update:value", this.searchTerm);
      this.$emit("field-change");
      this.pickCity("");
      this.resultsVisible = false;
    },
  },
});
</script>
